import React from "react";

import { FileText, Plus, X } from "lucide-react";

import { generateQuestionId } from "../../../../Utils/Helper/idGenerator";
import XFactorLevelGuide from "../XFactorLevelGuide";

interface Step2Props {
  template: any;
  questions: any[];
  customQuestions: any[];
  organizationLevels: any[];
  onUpdateCustomQuestions: (questions: any[]) => void;
}

const Step2QuestionPreview: React.FC<Step2Props> = ({
  template,
  questions,
  customQuestions,
  organizationLevels,
  onUpdateCustomQuestions,
}) => {
  const handleAddCustomQuestion = () => {
    const newQuestion = {
      id: generateQuestionId(),
      question: "",
      type: "textarea",
      required: false,
      placeholder: "",
      options: null,
    };
    onUpdateCustomQuestions([...customQuestions, newQuestion]);
  };

  const handleUpdateCustomQuestion = (questionId: string, updates: any) => {
    onUpdateCustomQuestions(
      customQuestions.map(q => {
        if (q.id === questionId) {
          const updated = { ...q, ...updates };
          // Initialize options when type changes to dropdown/multiple_choice/scale
          if (
            (updates.type === "dropdown" ||
              updates.type === "multiple_choice" ||
              updates.type === "scale") &&
            !updated.options
          ) {
            updated.options = [
              { label: "", value: "" },
              { label: "", value: "" },
            ];
          }
          // Clear options if type changes to textarea
          if (updates.type === "textarea" && updated.options) {
            updated.options = null;
          }
          return updated;
        }
        return q;
      })
    );
  };

  const handleUpdateCustomQuestionOption = (
    questionId: string,
    optionIndex: number,
    field: "label" | "value",
    value: string
  ) => {
    onUpdateCustomQuestions(
      customQuestions.map(q => {
        if (q.id === questionId && q.options) {
          const newOptions = [...q.options];
          newOptions[optionIndex] = {
            ...newOptions[optionIndex],
            [field]: value,
          };
          return { ...q, options: newOptions };
        }
        return q;
      })
    );
  };

  const handleAddCustomQuestionOption = (questionId: string) => {
    onUpdateCustomQuestions(
      customQuestions.map(q => {
        if (q.id === questionId) {
          return {
            ...q,
            options: [...(q.options || []), { label: "", value: "" }],
          };
        }
        return q;
      })
    );
  };

  const handleRemoveCustomQuestionOption = (
    questionId: string,
    optionIndex: number
  ) => {
    onUpdateCustomQuestions(
      customQuestions.map(q => {
        if (q.id === questionId && q.options) {
          return {
            ...q,
            options: q.options.filter((_: any, i: number) => i !== optionIndex),
          };
        }
        return q;
      })
    );
  };

  const handleDeleteCustomQuestion = (questionId: string) => {
    onUpdateCustomQuestions(customQuestions.filter(q => q.id !== questionId));
  };

  if (!template) return null;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Question Preview
        </h3>
        <p className="text-gray-600">
          Review the questions that will be included in this assessment
        </p>
      </div>
      <div className="max-h-96 overflow-y-auto">
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2 text-lg">
              {template.name}
            </h4>
            <p className="text-sm text-blue-700">{template.description}</p>
            <div className="mt-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {template.category === "short"
                  ? "Short Assessment"
                  : "Long Assessment"}
              </span>
            </div>
          </div>

          {/* XFACTOR Framework Guide */}
          {template.category === "short" && (
            <div className="space-y-2">
              <h5 className="font-medium text-gray-900 text-lg">
                Level Assessment Framework
              </h5>
              <XFactorLevelGuide
                showHeader={true}
                compact={false}
                className="mb-4"
                organizationLevels={organizationLevels || []}
              />
              <p className="text-sm text-gray-600">
                Use this framework when selecting the maturity level for each
                account in your assessment.
              </p>
            </div>
          )}

          <div className="space-y-4">
            <h5 className="font-medium text-gray-900">
              Template Questions (Read-only)
            </h5>
            {questions.map((question: any, index: number) => (
              <div
                key={question.id || index}
                className="bg-gray-50 p-4 rounded-lg"
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="text-sm font-medium text-gray-500">
                    Question {index + 1}
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Izba Template
                  </span>
                </div>
                <div className="text-gray-900 mb-3">
                  {question.question || question.question_text}
                  {question.required && (
                    <span className="inline-flex items-center px-2 py-1 ml-2 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Required
                    </span>
                  )}
                </div>

                {/* Response Field Type Display */}
                <div className="mb-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Response Type:
                    </span>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        question.type === "scale"
                          ? "bg-orange-100 text-orange-800"
                          : question.type === "dropdown"
                            ? "bg-blue-100 text-blue-800"
                            : question.type === "multiple_choice"
                              ? "bg-purple-100 text-purple-800"
                              : question.type === "textarea"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {question.type === "scale"
                        ? "Scale Rating"
                        : question.type === "dropdown"
                          ? "Single Choice"
                          : question.type === "multiple_choice"
                            ? "Multiple Choice"
                            : question.type === "textarea"
                              ? "Free Text"
                              : question.type}
                    </span>
                  </div>

                  {/* Field-specific details */}
                  {question.type === "scale" && question.options && (
                    <div className="text-sm text-gray-600 bg-gray-100 p-2 rounded">
                      <div className="flex items-center space-x-4">
                        <span>
                          Range: {question.options.min || 1} -{" "}
                          {question.options.max || 10}
                        </span>
                        {question.options.labels && (
                          <span>
                            Labels: {question.options.labels[0]} -{" "}
                            {question.options.labels[1]}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        Users will see a slider or radio buttons for selection
                      </div>
                    </div>
                  )}

                  {question.type === "dropdown" && question.options && (
                    <div className="text-sm text-gray-600 bg-gray-100 p-2 rounded">
                      <div className="mb-1">Available options:</div>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        {question.options.map((opt: any, optIndex: number) => (
                          <div
                            key={optIndex}
                            className="flex items-center space-x-1"
                          >
                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            <span>{opt.label || opt.value || opt}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        Users will see a dropdown menu for selection
                      </div>
                    </div>
                  )}

                  {question.type === "multiple_choice" && question.options && (
                    <div className="text-sm text-gray-600 bg-gray-100 p-2 rounded">
                      <div className="mb-1">
                        Available options (multiple selection):
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        {question.options.map((opt: any, optIndex: number) => (
                          <div
                            key={optIndex}
                            className="flex items-center space-x-1"
                          >
                            <span className="w-2 h-2 border-2 border-purple-500 rounded flex items-center justify-center">
                              <span className="w-1 h-1 bg-purple-500 rounded"></span>
                            </span>
                            <span>{opt.label || opt.value || opt}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        Users can select multiple options using checkboxes
                      </div>
                    </div>
                  )}

                  {question.type === "textarea" && (
                    <div className="text-sm text-gray-600 bg-gray-100 p-2 rounded">
                      <div className="flex items-center space-x-2">
                        <span>Multi-line text input</span>
                        {question.placeholder && (
                          <span className="text-xs text-gray-500">
                            Placeholder: {question.placeholder}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        Users can enter detailed text responses
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Custom Questions Section */}
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div className="flex items-center justify-between">
              <h5 className="font-medium text-gray-900">Custom Questions</h5>
              <button
                onClick={handleAddCustomQuestion}
                className="inline-flex items-center px-3 py-1 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Custom Question
              </button>
            </div>

            {customQuestions.length === 0 ? (
              <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
                <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  No custom questions added yet
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Click Add Custom Question to add your own questions
                </p>
              </div>
            ) : (
              customQuestions.map((question, index) => (
                <div key={question.id} className="p-4 rounded-lg border">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-gray-500">
                        Custom Question {index + 1}
                      </span>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={question.required || false}
                          onChange={e =>
                            handleUpdateCustomQuestion(question.id, {
                              required: e.target.checked,
                            })
                          }
                          onClick={e => e.stopPropagation()}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded cursor-pointer"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Required
                        </span>
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Custom
                      </span>
                      <button
                        onClick={() => handleDeleteCustomQuestion(question.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Question Text
                      </label>
                      <input
                        type="text"
                        value={question.question}
                        onChange={e =>
                          handleUpdateCustomQuestion(question.id, {
                            question: e.target.value,
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Enter your custom question..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Response Type
                      </label>
                      <select
                        value={question.type}
                        onChange={e =>
                          handleUpdateCustomQuestion(question.id, {
                            type: e.target.value,
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="textarea">Free Text</option>
                        <option value="dropdown">Single Choice</option>
                        <option value="multiple_choice">Multiple Choice</option>
                        <option value="scale">Scale</option>
                      </select>
                    </div>
                    {question.type === "textarea" && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Placeholder Text
                        </label>
                        <input
                          type="text"
                          value={question.placeholder || ""}
                          onChange={e =>
                            handleUpdateCustomQuestion(question.id, {
                              placeholder: e.target.value,
                            })
                          }
                          className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Enter placeholder text..."
                        />
                      </div>
                    )}
                    {(question.type === "dropdown" ||
                      question.type === "multiple_choice" ||
                      question.type === "scale") && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Options
                        </label>
                        <div className="space-y-2">
                          {(question.options || []).map(
                            (option: any, optIndex: number) => (
                              <div
                                key={optIndex}
                                className="flex items-center gap-2"
                              >
                                <input
                                  type="text"
                                  value={option.label || ""}
                                  onChange={e =>
                                    handleUpdateCustomQuestionOption(
                                      question.id,
                                      optIndex,
                                      "label",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Option label"
                                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                />
                                <input
                                  type="text"
                                  value={option.value || ""}
                                  onChange={e =>
                                    handleUpdateCustomQuestionOption(
                                      question.id,
                                      optIndex,
                                      "value",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Option value"
                                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                />
                                <button
                                  onClick={() =>
                                    handleRemoveCustomQuestionOption(
                                      question.id,
                                      optIndex
                                    )
                                  }
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            )
                          )}
                          <button
                            onClick={() =>
                              handleAddCustomQuestionOption(question.id)
                            }
                            className="inline-flex items-center px-3 py-1 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Option
                          </button>
                        </div>
                        {question.type === "scale" && (
                          <div className="mt-2 text-xs text-gray-500">
                            For scale questions, you can also specify min/max
                            values in the options
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2QuestionPreview;
